import { Fragment, useId, useState } from 'react'
import Icon from './Icon'
import styles from './HomeOutcomePerformancePanel.module.css'

// Each tab's set of keys into a metric blob. Every backend metric (Gross
// MTTR, Net MTTR, ...) writes MTD/3MONTH/BASELINETODATE + matching
// *_change/*_RANGE/*_CASES keys (see gaussiq-intelligence's
// core/metrics/generate.py), except the current ENG Assist stub, which uses
// BASETODATE/DATE_RANGE instead and has no case count — `alt` picks up the
// naming split so one stray metric shape doesn't blank a whole column, and
// the footer's case count simply doesn't render when absent.
const PERIODS = [
  { id: 'MTD', label: 'MTD', value: 'MTD', change: 'MTD_change', range: 'MTD_RANGE', cases: 'MTD_CASES' },
  { id: '3MONTH', label: '3 Months', value: '3MONTH', change: '3MONTH_change', range: '3MONTH_RANGE', altRange: '3MO_RANGE', cases: '3MONTH_CASES' },
  { id: 'BASELINETODATE', label: 'Baseline to Date', value: 'BASELINETODATE', altValue: 'BASETODATE', change: 'BASELINE_change', altChange: 'BASETODATE_change', range: 'BASELINE_RANGE', altRange: 'DATE_RANGE', cases: 'BASELINETODATE_CASES' },
]

function field(metric, key, altKey) {
  if (metric[key] !== undefined && metric[key] !== null && metric[key] !== '') return metric[key]
  return altKey ? metric[altKey] : undefined
}

function isOnTarget(changeText) {
  return /faster|decrease/i.test(changeText ?? '')
}

// The backend's own description for NET_MTTR ("MTTR - Customer Wait Time")
// describes the calculation, not the metric in plain terms — the tooltip
// wants the latter, same as Gross MTTR's.
const TOOLTIP_TEXT_OVERRIDES = {
  NET_MTTR: 'Mean Time to Resolution',
}

/**
 * HomeOutcomePerformancePanel — the Home tab hero banner's left panel,
 * replacing the old fixed executive-summary copy with the real outcome
 * metrics gaussiq-intelligence now computes (GET /api/metrics/outcome):
 * a period switch (MTD / 3 Months / Baseline to Date) over a row of
 * MTTR-style stat columns, each with its baseline comparison.
 *
 * Props:
 *   metrics {Array<{name: string, tag: string, metric: object}>} — from
 *            fetchOutcomeMetrics(); each `metric` blob is passed through
 *            from the backend untranslated (see src/api/metrics.js).
 */
export default function HomeOutcomePerformancePanel({ metrics = [] }) {
  const [periodId, setPeriodId] = useState('BASELINETODATE')
  const period = PERIODS.find(p => p.id === periodId)
  const tooltipBaseId = useId()

  const rows = metrics.map(({ name, metric }) => {
    const value = field(metric, period.value, period.altValue)
    const change = field(metric, period.change, period.altChange)
    return {
      key: name,
      label: metric.name,
      description: TOOLTIP_TEXT_OVERRIDES[name] ?? metric.description,
      unit: metric.label,
      value,
      baseline: metric.BASELINE,
      change,
      onTarget: isOnTarget(change),
    }
  })

  const footerMetric = metrics[0]?.metric
  const footerRange = footerMetric ? field(footerMetric, period.range, period.altRange) : null
  const footerBaselineDate = footerMetric?.BASELINE_DATE
  const footerCases = footerMetric ? field(footerMetric, period.cases) : null

  const footerParts = [
    footerBaselineDate && `Baseline: ${footerBaselineDate}`,
    footerRange && `Data Range: ${footerRange}`,
    (footerCases || footerCases === 0) && `${Number(footerCases).toLocaleString()} Cases`,
  ].filter(Boolean)

  return (
    <div className={styles.panel}>
      <div className={styles.titleBlock}>
        <h2 className={styles.title}>Customer Outcome Performance</h2>
        <p className={styles.subtitle}>Outcome metrics vs. your agreed baseline</p>
      </div>

      <div className={styles.periodTabs} role="tablist">
        {PERIODS.map(p => (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={p.id === periodId}
            className={`${styles.periodTab} ${p.id === periodId ? styles.periodTabActive : ''}`}
            onClick={() => setPeriodId(p.id)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <p className={styles.emptyState}>Outcome metrics will appear here once available.</p>
      ) : (
        <>
          <div className={styles.statRow}>
            {rows.map((row, i) => {
              const tooltipId = `${tooltipBaseId}-${row.key}`
              const align = i === 0 ? styles.tooltipLeft : i === rows.length - 1 ? styles.tooltipRight : styles.tooltipCenter
              return (
              <div key={row.key} className={styles.statCol}>
                <div className={styles.labelRow}>
                  <span className={styles.statLabel}>{row.label}</span>
                  {row.description && (
                    <span className={styles.infoWrap}>
                      <button
                        type="button"
                        className={styles.infoBtn}
                        aria-label={`About ${row.label}`}
                        aria-describedby={tooltipId}
                      >
                        <Icon name="info" />
                      </button>
                      <div id={tooltipId} className={`${styles.tooltip} ${align}`} role="tooltip">
                        {row.description}
                      </div>
                    </span>
                  )}
                </div>
                <span className={styles.statValue}>
                  {row.value ?? '—'}
                  {row.unit && <span className={styles.statUnit}> {row.unit}</span>}
                </span>
                {row.baseline !== undefined && row.baseline !== null && (
                  <span className={styles.statBaseline}>
                    Baseline: {row.baseline}{row.unit === '%' && '%'}
                  </span>
                )}
                {row.change && (
                  <span className={`${styles.statChange} ${row.onTarget ? styles.good : styles.bad}`}>
                    {row.change}
                  </span>
                )}
              </div>
              )
            })}
          </div>

          {footerParts.length > 0 && (
            <div className={styles.footer}>
              {footerParts.map((part, i) => (
                <Fragment key={part}>
                  {i > 0 && <span className={styles.footerDot}>•</span>}
                  <span>{part}</span>
                </Fragment>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
